/**
 * PageBrowserModal
 *
 * Modal for browsing, organizing, and navigating project pages.
 * Features folder sidebar, search/filter, sort, starring, and page switching.
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Search,
  FolderPlus,
  FolderOpen,
  Folder,
  MoreVertical,
  Pencil,
  Trash2,
  FolderInput,
  ArrowUpDown,
  Check,
  Star,
  Home,
  FileText,
  LayoutGrid,
  Copy,
  X,
} from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/stores/auth/auth-store';
import { useEditorStore } from '@/stores/editor/editor-store';
import { PageTemplate } from '@/types';
import {
  FOREST_GREEN,
  MID_GRAY,
  BORDER,
  WHITE,
} from '@/styles/tokens';
import type {
  UsePageBrowserReturn,
  PageSortBy,
} from '@/hooks/editor/usePageBrowser';
import { FOLDER_ALL, FOLDER_STARRED, FOLDER_UNCATEGORIZED } from '@/hooks/editor/usePageBrowser';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getTemplateIcon = (template?: PageTemplate) => {
  switch (template) {
    case PageTemplate.DASHBOARD:
      return '📊';
    case PageTemplate.REPORT:
      return '📋';
    case PageTemplate.FORM:
      return '📝';
    case PageTemplate.ANALYTICS:
      return '📈';
    default:
      return '📄';
  }
};

const formatRelativeTime = (date: Date | string | undefined): string => {
  if (!date) return '';
  const now = Date.now();
  const then = new Date(date).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  return new Date(date).toLocaleDateString();
};

// ---------------------------------------------------------------------------
// Sort options
// ---------------------------------------------------------------------------

const SORT_OPTIONS: { value: PageSortBy; label: string }[] = [
  { value: 'order', label: 'Page Order' },
  { value: 'name', label: 'Name A-Z' },
  { value: 'updatedAt', label: 'Last Modified' },
  { value: 'componentCount', label: 'Components' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PageBrowserModal: React.FC<UsePageBrowserReturn> = (props) => {
  const {
    isOpen,
    closeModal,
    allPages,
    filteredPages,
    navigationGroups,
    starredCount,
    totalCount,
    folderPageCounts,
    uncategorizedCount,
    selectedFolderId,
    searchQuery,
    sortBy,
    sortOrder,
    setSelectedFolderId,
    setSearchQuery,
    setSortBy,
    setSortOrder,
    toggleStar,
    switchToPage,
    assignToFolder,
    createFolder,
    renameFolder,
    deleteFolder,
    currentPageId,
    getPageComponentCount,
  } = props;

  // Auth store for home page
  const homeProjectId = useAuthStore((state) => state.user?.preferences?.homeProjectId);
  const homePageId = useAuthStore((state) => state.user?.preferences?.homePageId);
  const project = useEditorStore((state) => state.project);
  const duplicatePage = useEditorStore((state) => state.duplicatePage);
  const isCurrentProjectHome = project?.id ? homeProjectId === project.id : false;
  const isPageHome = (pageId: string) => isCurrentProjectHome && homePageId === pageId;

  // Local UI state
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [folderRenameValue, setFolderRenameValue] = useState('');

  const newFolderInputRef = useRef<HTMLInputElement>(null);
  const folderRenameInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus inputs
  useEffect(() => {
    if (showNewFolderInput && newFolderInputRef.current) {
      newFolderInputRef.current.focus();
    }
  }, [showNewFolderInput]);

  useEffect(() => {
    if (renamingFolderId && folderRenameInputRef.current) {
      folderRenameInputRef.current.focus();
    }
  }, [renamingFolderId]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Small delay so Dialog animation finishes
      const t = setTimeout(() => searchInputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isOpen]);

  // Reset local state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowNewFolderInput(false);
      setNewFolderName('');
      setRenamingFolderId(null);
    }
  }, [isOpen]);

  const handleCreateFolder = () => {
    if (newFolderName.trim()) {
      createFolder(newFolderName.trim());
      setNewFolderName('');
      setShowNewFolderInput(false);
    }
  };

  const handleRenameFolder = (groupId: string) => {
    if (folderRenameValue.trim()) {
      renameFolder(groupId, folderRenameValue.trim());
      setRenamingFolderId(null);
    }
  };

  const handleDeleteFolder = (groupId: string) => {
    deleteFolder(groupId);
  };

  const handleDuplicate = (pageId: string, pageName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    duplicatePage(pageId, `${pageName} Copy`);
  };

  // Active folder style
  const folderBtnClass = (isActive: boolean) =>
    `flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs w-full text-left transition-colors ${
      isActive
        ? 'bg-primary/10 text-primary font-medium'
        : 'text-muted-foreground hover:bg-accent'
    }`;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent variant="light" className="sm:max-w-3xl p-0 gap-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Pages</h2>
            <p className="text-[11px] text-muted-foreground">
              {totalCount} page{totalCount !== 1 ? 's' : ''} in project
            </p>
          </div>
        </div>

        {/* Search + Sort bar */}
        <div className="flex items-center gap-2 px-4 pb-3 border-b border-border">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search pages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-3 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1 rounded-md border border-border px-2 py-1.5 text-xs text-muted-foreground hover:bg-accent shrink-0">
                <ArrowUpDown className="h-3 w-3" />
                Sort
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              {SORT_OPTIONS.map((opt) => (
                <DropdownMenuItem
                  key={opt.value}
                  onClick={() => {
                    if (sortBy === opt.value) {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortBy(opt.value);
                      setSortOrder('asc');
                    }
                  }}
                  className="text-xs"
                >
                  {opt.label}
                  {sortBy === opt.value && (
                    <Check className="ml-auto h-3 w-3" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Main content: sidebar + list */}
        <div className="flex flex-1 min-h-0" style={{ height: '60vh' }}>
          {/* Folder sidebar */}
          <div className="w-40 border-r border-border flex flex-col py-2 px-2 shrink-0 overflow-y-auto">
            {/* All */}
            <button
              onClick={() => setSelectedFolderId(FOLDER_ALL)}
              className={folderBtnClass(selectedFolderId === FOLDER_ALL)}
            >
              <FolderOpen className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate flex-1">All</span>
              <span className="text-[10px] text-muted-foreground">{totalCount}</span>
            </button>

            {/* Starred */}
            {starredCount > 0 && (
              <button
                onClick={() => setSelectedFolderId(FOLDER_STARRED)}
                className={folderBtnClass(selectedFolderId === FOLDER_STARRED)}
              >
                <Star className="h-3.5 w-3.5 shrink-0 text-yellow-500" fill={selectedFolderId === FOLDER_STARRED ? 'currentColor' : 'none'} />
                <span className="truncate flex-1">Starred</span>
                <span className="text-[10px] text-muted-foreground">{starredCount}</span>
              </button>
            )}

            {(navigationGroups.length > 0 || starredCount > 0) && (
              <div className="border-t border-border my-1" />
            )}

            {/* Folders */}
            {navigationGroups.map((group) => (
              <div key={group.id} className="group relative">
                {renamingFolderId === group.id ? (
                  <div className="flex items-center gap-1 px-1 py-1">
                    <input
                      ref={folderRenameInputRef}
                      value={folderRenameValue}
                      onChange={(e) => setFolderRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenameFolder(group.id);
                        if (e.key === 'Escape') setRenamingFolderId(null);
                      }}
                      onBlur={() => setRenamingFolderId(null)}
                      className="w-full rounded border border-primary/50 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => setSelectedFolderId(group.id)}
                    className={`${folderBtnClass(selectedFolderId === group.id)} pr-6`}
                  >
                    <Folder className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate flex-1">{group.name}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      {folderPageCounts[group.id] || 0}
                    </span>
                  </button>
                )}
                {/* Folder actions (on hover) */}
                {renamingFolderId !== group.id && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-gray-200">
                        <MoreVertical className="h-3 w-3 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-32">
                      <DropdownMenuItem
                        onClick={() => {
                          setRenamingFolderId(group.id);
                          setFolderRenameValue(group.name);
                        }}
                        className="text-xs"
                      >
                        <Pencil className="h-3 w-3 mr-1.5" />
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDeleteFolder(group.id)}
                        className="text-xs text-red-600 focus:text-red-600"
                      >
                        <Trash2 className="h-3 w-3 mr-1.5" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            ))}

            {/* Uncategorized */}
            <button
              onClick={() => setSelectedFolderId(FOLDER_UNCATEGORIZED)}
              className={folderBtnClass(selectedFolderId === FOLDER_UNCATEGORIZED)}
            >
              <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate flex-1 italic">Uncategorized</span>
              <span className="text-[10px] text-muted-foreground">{uncategorizedCount}</span>
            </button>

            {/* New folder */}
            {showNewFolderInput ? (
              <div className="flex items-center gap-1 px-1 py-1 mt-1">
                <input
                  ref={newFolderInputRef}
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateFolder();
                    if (e.key === 'Escape') {
                      setNewFolderName('');
                      setShowNewFolderInput(false);
                    }
                  }}
                  onBlur={() => {
                    if (!newFolderName.trim()) {
                      setShowNewFolderInput(false);
                    }
                  }}
                  placeholder="Folder name"
                  className="w-full rounded border border-primary/50 px-1.5 py-0.5 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
            ) : (
              <button
                onClick={() => setShowNewFolderInput(true)}
                className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs text-emerald-600 hover:bg-emerald-50 mt-1"
              >
                <FolderPlus className="h-3.5 w-3.5" />
                New Folder
              </button>
            )}
          </div>

          {/* Page list */}
          <div className="flex-1 overflow-y-auto">
            {filteredPages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <FileText className="h-8 w-8 text-muted-foreground/50" />
                <p className="text-xs text-muted-foreground">
                  {searchQuery ? 'No pages match your search' : 'No pages in this folder'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {filteredPages.map((page) => {
                  const isCurrent = page.id === currentPageId;
                  const isHome = isPageHome(page.id);
                  const componentCount = getPageComponentCount(page.id);
                  const folderGroup = navigationGroups.find(
                    (g) => g.id === page.navigation?.navigationGroup,
                  );

                  return (
                    <div
                      key={page.id}
                      className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${
                        isCurrent
                          ? 'bg-primary/5'
                          : 'hover:bg-accent'
                      }`}
                      onClick={() => switchToPage(page.id)}
                    >
                      {/* Template icon */}
                      <span className="text-base shrink-0" role="img">
                        {getTemplateIcon(page.template)}
                      </span>

                      {/* Name + metadata */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`text-sm truncate ${
                              isCurrent ? 'font-semibold text-primary' : 'font-medium text-foreground'
                            }`}
                          >
                            {page.name}
                          </span>
                          {isHome && (
                            <Home className="h-3 w-3 text-primary shrink-0" />
                          )}
                          {page.isDefault && (
                            <span className="text-[9px] px-1 py-0.5 rounded bg-muted text-muted-foreground shrink-0">
                              Default
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          {page.description && (
                            <span className="text-[11px] text-muted-foreground truncate max-w-[200px]">
                              {page.description}
                            </span>
                          )}
                          {folderGroup && (
                            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5 shrink-0">
                              <Folder className="h-2.5 w-2.5" />
                              {folderGroup.name}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Component count */}
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0 tabular-nums">
                        {componentCount}
                      </span>

                      {/* Last modified */}
                      {page.updatedAt && (
                        <span className="text-[10px] text-muted-foreground shrink-0 w-14 text-right">
                          {formatRelativeTime(page.updatedAt)}
                        </span>
                      )}

                      {/* Star toggle */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStar(page.id);
                        }}
                        className={`p-1 rounded-md transition-colors shrink-0 ${
                          page.isStarred
                            ? 'text-yellow-500 hover:text-yellow-600'
                            : 'text-muted-foreground/50 hover:text-muted-foreground'
                        }`}
                        title={page.isStarred ? 'Remove from starred' : 'Add to starred'}
                      >
                        <Star
                          className="h-3.5 w-3.5"
                          fill={page.isStarred ? 'currentColor' : 'none'}
                        />
                      </button>

                      {/* Context menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent shrink-0"
                          >
                            <MoreVertical className="h-3.5 w-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              switchToPage(page.id);
                            }}
                            className="text-xs"
                          >
                            <LayoutGrid className="h-3 w-3 mr-1.5" />
                            Switch to Page
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />

                          {/* Move to Folder submenu */}
                          {navigationGroups.length > 0 && (
                            <>
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger className="text-xs">
                                  <FolderInput className="h-3 w-3 mr-1.5" />
                                  Move to Folder
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent className="w-36">
                                  {navigationGroups.map((group) => (
                                    <DropdownMenuItem
                                      key={group.id}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        assignToFolder(page.id, group.id);
                                      }}
                                      className="text-xs"
                                    >
                                      <Folder className="h-3 w-3 mr-1.5" />
                                      {group.name}
                                      {page.navigation?.navigationGroup === group.id && (
                                        <Check className="ml-auto h-3 w-3" />
                                      )}
                                    </DropdownMenuItem>
                                  ))}
                                  {page.navigation?.navigationGroup && (
                                    <>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          assignToFolder(page.id, undefined);
                                        }}
                                        className="text-xs text-muted-foreground"
                                      >
                                        <X className="h-3 w-3 mr-1.5" />
                                        Remove from Folder
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                              <DropdownMenuSeparator />
                            </>
                          )}

                          <DropdownMenuItem
                            onClick={(e) => handleDuplicate(page.id, page.name, e)}
                            className="text-xs"
                          >
                            <Copy className="h-3 w-3 mr-1.5" />
                            Duplicate
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
